'use client';

import React, { ReactNode, useState } from 'react';
import Link from '@atoms/Link/Link';
import { cmsAlignToFlexAlign, cmsAlignToSectionAlign, cmsAlignToTextAlign } from '@helpers/cmsComponentHelpers';
import YoutubeModal from '@molecules/YoutubeModal/YoutubeModal';
import getPaddingFromCms from '@helpers/getPaddingFromCms';
import { useTranslations } from 'next-intl';
import { isCommonStoreMessageKey } from '@helpers/commonTranslation';
import HorizontalBannerComponentSkeleton from '@molecules/HorizontalBannerComponent/HorizontalBannerComponent.skeleton';
import DOMPurify from 'isomorphic-dompurify';
import Video from '@icons/video.svg';
import useCmsStoreContent from '@molecules/HorizontalBannerComponent/useCmsStoreData';
import type { ComponentWsDTO } from '@occ/api-client';
import useResponsive from '@hooks/useResponsive';
import TrackedComponent from '@organisms/TrackedComponent/TrackedComponent';
import useSymplifyExperiment from '@utility/ABTest/Symplify/useSymplifyExperiment';
import { useSearchParams } from 'next/navigation';
import {
  StyledButton,
  StyledDiv,
  StyledHeading,
  StyledPreamble,
  StyledSection,
  StyledText,
  StyledYoutubeIcon,
} from './HorizontalBannerComponent.styles';

interface Props {
  data: ComponentWsDTO;
  logImpression?: () => void;
  onClick?: () => void;
}

const HorizontalBannerComponent = ({ data, logImpression, onClick }: Props) => {
  const t = useTranslations('common');
  const searchParams = useSearchParams();
  const { isClient } = useResponsive();
  const [showYoutubeModal, setShowYoutubeModal] = useState(false);
  const variant = useSymplifyExperiment('Hero Banner Button Text', 'Original');
  const currentQuery = searchParams ? Object.fromEntries(searchParams.entries()) : {};

  const { titleContent, preambleContent, isLoading, horizontalBannerData } = useCmsStoreContent(
    data as HorizontalBannerType
  );

  const alignItems = cmsAlignToFlexAlign(horizontalBannerData?.textAlign);
  const textAlign = cmsAlignToTextAlign(horizontalBannerData?.textAlign);
  const alignSection = cmsAlignToSectionAlign(horizontalBannerData?.buttonPosition);
  const isTitleBanner = horizontalBannerData?.titleBanner === 'true';
  const isButtonEnabled = horizontalBannerData?.buttonEnabled === 'true';

  const button = (
    <StyledButton
      background={horizontalBannerData?.buttonColor}
      color={horizontalBannerData?.buttonTextColor}
      title={horizontalBannerData?.buttonText}
      onClick={() => {
        if (horizontalBannerData?.youtubeId) setShowYoutubeModal(true);
      }}
    >
      {horizontalBannerData?.youtubeId && (
        <StyledYoutubeIcon svg={Video} size={20} customColor={horizontalBannerData?.buttonTextColor} />
      )}
      {variant != 'Original' && horizontalBannerData?.buttonText === 'Till varorna'
        ? t('heroBannerABTestButtonText')
        : horizontalBannerData?.buttonText}
    </StyledButton>
  );

  const wrapWithLink = (content: ReactNode) => {
    const queryString = new URLSearchParams(currentQuery as any).toString();
    const urlLink = horizontalBannerData?.urlLink || '';
    const href = urlLink && queryString ? `${urlLink}?${queryString}` : urlLink;

    return (
      <Link
        href={href}
        target={horizontalBannerData?.urlLinkInNewWindow === 'true' ? '_blank' : ''}
        data-testid="horizontalbanner-link"
      >
        {content}
      </Link>
    );
  };

  const banner = (
    <StyledSection
      $fixed={isTitleBanner}
      $clickable={!isButtonEnabled && !!horizontalBannerData?.urlLink}
      $padding={getPaddingFromCms(horizontalBannerData)}
      style={{
        backgroundColor: horizontalBannerData?.backgroundColor,
        alignItems: alignSection,
      }}
      $media={horizontalBannerData?.media}
      onClick={onClick}
    >
      <StyledDiv
        style={{
          textAlign,
          alignItems,
        }}
      >
        {titleContent && (
          <StyledHeading
            color={horizontalBannerData?.titleTextColor as HeadingColors}
            variant={horizontalBannerData?.fontType === 'SMALL' ? 'h2' : 'h1'}
            size={!!horizontalBannerData?.fontType && horizontalBannerData?.fontType === 'SMALL' ? undefined : 'large'}
          >
            {titleContent}
          </StyledHeading>
        )}
        {horizontalBannerData?.openingHoursMessageKey && horizontalBannerData?.openingStoreMessageValue && (
          <StyledText color={horizontalBannerData?.titleTextColor as HeadingColors} type="label" size="medium">
            {isCommonStoreMessageKey(horizontalBannerData.openingHoursMessageKey)
              ? `${t(horizontalBannerData.openingHoursMessageKey)} ${
                  typeof horizontalBannerData.openingStoreMessageValue === 'string'
                    ? horizontalBannerData.openingStoreMessageValue
                    : ''
                }`
              : horizontalBannerData.openingHoursMessageKey}
          </StyledText>
        )}
        {preambleContent && (
          <StyledText
            style={{ color: horizontalBannerData?.preambleTextColor }}
            type="intro"
            $fontType={horizontalBannerData?.fontType?.toLowerCase()}
          >
            <StyledPreamble dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(preambleContent) }} />
          </StyledText>
        )}
        {isButtonEnabled && !isTitleBanner && (horizontalBannerData?.urlLink ? wrapWithLink(button) : button)}
      </StyledDiv>
    </StyledSection>
  );

  return (
    <div>
      {isLoading ? (
        <HorizontalBannerComponentSkeleton />
      ) : !isButtonEnabled && horizontalBannerData?.urlLink ? (
        wrapWithLink(banner)
      ) : (
        banner
      )}
      {showYoutubeModal && (
        <YoutubeModal youtubeId={horizontalBannerData?.youtubeId} setShowModal={setShowYoutubeModal} />
      )}
      {isClient && logImpression && <TrackedComponent onInView={logImpression} />}
    </div>
  );
};

export default HorizontalBannerComponent;
